// src/pages/ProfilePage.js
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../utils/axiosInstance";
import LanguageSwitch from "../components/LanguageSwitch";
import "../styles/ProfilePage.css";

const safe = (v, fallback = "—") =>
  v === undefined || v === null || v === "" ? fallback : v;

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Silme talebi için olası endpoint’ler (projende hangisi varsa o çalışır)
const DELETE_REQUEST_ENDPOINTS = [
  "/api/auth/delete-account-request/",
  "/api/auth/request-account-deletion/",
  "/api/auth/account-deletion-request/",
];

export default function ProfilePage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [profile, setProfileState] = useState(null);

  // UI mesajları
  const [toast, setToast] = useState({ type: "", text: "" }); // type: success|error|info

  // Delete modal
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // LocalStorage fallback (backend endpoint yoksa bile boş kalmasın)
  const fallbackProfile = useMemo(() => {
    const role = localStorage.getItem("profile_role") || "doctor";
    const canPost = localStorage.getItem("profile_can_post") === "1";

    return {
      username: localStorage.getItem("last_username") || "kullanici",
      email: localStorage.getItem("last_email") || "—",
      fullName: localStorage.getItem("last_fullname") || "—",
      role,
      can_post: canPost,
      verification_status: localStorage.getItem("verification_status") || "none",
      credit_balance: Number(localStorage.getItem("credit_balance") || 0),
      document: localStorage.getItem("document_name") || "",
      created_at: localStorage.getItem("created_at") || "",
      membership_active_until: localStorage.getItem("membership_active_until") || "",
    };
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get("/api/auth/me/");
        const d = res?.data || {};

        const mapped = {
          username: d.username || d.user?.username,
          email: d.email || d.user?.email,
          fullName:
            d.full_name ||
            [d.first_name, d.last_name].filter(Boolean).join(" ") ||
            "",
          role: d.role || d.profile?.role,
          can_post: !!(d.can_post ?? d.profile?.can_post),
          verification_status:
            d.verification_status || d.profile?.verification_status || "none",
          credit_balance: Number(d.credit_balance ?? d.credits ?? 0),
          document: d.document || d.profile?.document || "",
          created_at: d.created_at || d.date_joined || "",
          membership_active_until:
            d.membership_active_until || d.profile?.membership_active_until || "",
        };

        if (!alive) return;

        const merged = { ...fallbackProfile, ...mapped };
        setProfileState(merged);

        // mini cache
        if (merged.username) localStorage.setItem("last_username", merged.username);
        if (merged.email) localStorage.setItem("last_email", merged.email);
        if (merged.fullName) localStorage.setItem("last_fullname", merged.fullName);

        localStorage.setItem("profile_role", merged.role || fallbackProfile.role);
        localStorage.setItem("profile_can_post", merged.can_post ? "1" : "0");
        localStorage.setItem(
          "verification_status",
          merged.verification_status || "none"
        );
        localStorage.setItem(
          "credit_balance",
          String(Number(merged.credit_balance || 0))
        );
        localStorage.setItem("document_name", merged.document || "");
        localStorage.setItem("created_at", merged.created_at || "");
        localStorage.setItem(
          "membership_active_until",
          merged.membership_active_until || ""
        );
      } catch {
        if (!alive) return;
        setProfileState(fallbackProfile);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [fallbackProfile]);

  const roleLabel = (r) => {
    if (r === "doctor") return t("profilePage.roleDoctor");
    if (r === "clinic") return t("profilePage.roleClinic");
    if (r === "supplier") return t("profilePage.roleSupplier");
    return safe(r);
  };

  const badgeInfo = (s) => {
    const v = (s || "none").toLowerCase();
    if (v === "approved") return { text: t("profilePage.statusApproved"), cls: "ok" };
    if (v === "pending") return { text: t("profilePage.statusPending"), cls: "warn" };
    if (v === "rejected") return { text: t("profilePage.statusRejected"), cls: "bad" };
    return { text: t("profilePage.statusUnverified"), cls: "muted" };
  };

  const logout = () => {
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
    localStorage.setItem("isLoggedIn", "0");

    // Kullanıcıya bağlı cache temizliği
    localStorage.removeItem("last_username");
    localStorage.removeItem("last_email");
    localStorage.removeItem("last_fullname");
    localStorage.removeItem("profile_role");
    localStorage.removeItem("profile_can_post");
    localStorage.removeItem("verification_status");
    localStorage.removeItem("credit_balance");
    localStorage.removeItem("document_name");
    localStorage.removeItem("created_at");
    localStorage.removeItem("membership_active_until");

    window.location.href = "/login";
  };

  // Şifre değiştir: mevcut “Forgot Password” akışını kullan
  const goToPasswordReset = () => {
    window.location.href = "/forgot-password";
  };

  // Hesap silme talebi: backend’e “silme talebi” at
  const submitDeleteRequest = async () => {
    if (deleteLoading) return;

    const p = profile || fallbackProfile;
    const email = String(p.email || "").trim();

    if (!email || email === "—") {
      setToast({
        type: "error",
        text: t("profilePage.emailNotFound"),
      });
      setDeleteOpen(false);
      return;
    }

    setDeleteLoading(true);
    try {
      let lastErr = null;

      for (const url of DELETE_REQUEST_ENDPOINTS) {
        try {
          await axiosInstance.post(url, { email });
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
        }
      }

      if (lastErr) {
        const status = lastErr?.response?.status;
        const data = lastErr?.response?.data;
        console.log("DELETE REQUEST FAIL:", status, data);

        setToast({
          type: "error",
          text: t("profilePage.deleteRequestFailed"),
        });
        return;
      }

      setToast({
        type: "success",
        text: t("profilePage.deleteRequestReceived"),
      });
    } finally {
      setDeleteLoading(false);
      setDeleteOpen(false);
    }
  };

  const p = profile || fallbackProfile;
  const badge = badgeInfo(p.verification_status);

  return (
    <div className="profile-wrap">
      <div className="profile-header">
        <div>
          <h1 className="profile-title">{t("profilePage.title")}</h1>
          <p className="profile-subtitle">{t("profilePage.subtitle")}</p>
        </div>

        <button className="btn-ghost" onClick={logout}>
          {t("profilePage.logout")}
        </button>
      </div>

      {toast?.text ? (
        <div className={`toast ${toast.type || "info"}`} role="status">
          <span>{toast.text}</span>
          <button
            className="toast__x"
            onClick={() => setToast({ type: "", text: "" })}
            aria-label={t("profilePage.close")}
          >
            ×
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="profile-skeleton">{t("profilePage.loading")}</div>
      ) : (
        <>
          <div className="profile-grid">
            {/* Hesap */}
            <section className="card">
              <div className="card-title">{t("profilePage.accountInfo")}</div>

              <div className="row">
                <div className="label">{t("profilePage.username")}</div>
                <div className="value">{safe(p.username)}</div>
              </div>

              <div className="row">
                <div className="label">{t("profilePage.email")}</div>
                <div className="value">{safe(p.email)}</div>
              </div>

              <div className="row">
                <div className="label">{t("profilePage.fullName")}</div>
                <div className="value">{safe(p.fullName)}</div>
              </div>

              <div className="row">
                <div className="label">{t("profilePage.role")}</div>
                <div className="value">{roleLabel(p.role)}</div>
              </div>

              <div className="row">
                <div className="label">{t("profilePage.postPermission")}</div>
                <div className="value">
                  {p.can_post ? t("profilePage.active") : t("profilePage.inactive")}
                </div>
              </div>
            </section>

            {/* Doğrulama */}
            <section className="card">
              <div className="card-title">{t("profilePage.verification")}</div>

              <div className={`badge ${badge.cls}`}>{badge.text}</div>

              <div className="hint">{t("profilePage.verifiedHint")}</div>

              <div className="hint">{t("profilePage.verifiedHint2")}</div>
            </section>

            {/* Kredi */}
            <section className="card">
              <div className="card-title">{t("profilePage.creditPlan")}</div>

              <div className="big-number">{Number(p.credit_balance || 0)}</div>
              <div className="muted">{t("profilePage.currentCredit")}</div>

              <div className="hint">{t("profilePage.creditHint")}</div>

              <div className="actions">
                <button
                  className="btn-primary"
                  onClick={() => (window.location.href = "/buy-credits")}
                >
                  {t("profilePage.buyCredits")}
                </button>
              </div>
            </section>
          </div>

          <div className="profile-grid-2">
            <section className="card">
              <div className="card-title">{t("profilePage.security")}</div>
              <div className="hint">{t("profilePage.securityHint")}</div>

              <div className="actions">
                <button className="btn-secondary" onClick={goToPasswordReset}>
                  {t("profilePage.changePassword")}
                </button>
              </div>
            </section>

            <section className="card">
              <div className="card-title">{t("profilePage.account")}</div>

              <div className="row">
                <div className="label">{t("profilePage.membershipExpiry")}</div>
                <div className="value">
                  {formatDate(p.membership_active_until)}
                </div>
              </div>

              <div className="actions">
                <button className="btn-danger" onClick={() => setDeleteOpen(true)}>
                  {t("profilePage.deleteAccount")}
                </button>
              </div>
            </section>

            <section className="card">
              <div className="card-title">{t("profilePage.languagePref")}</div>
              <div className="hint">{t("profilePage.languagePrefHint")}</div>
              <div style={{ marginTop: 12 }}>
                <LanguageSwitch />
              </div>
            </section>
          </div>
        </>
      )}

      {deleteOpen ? (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={t("profilePage.deleteModalAriaLabel")}
        >
          <div className="modal">
            <div className="modal__title">{t("profilePage.deleteModalTitle")}</div>
            <div className="modal__hint">{t("profilePage.deleteModalHint")}</div>

            <div className="modal__actions">
              <button
                className="btn-secondary"
                onClick={() => setDeleteOpen(false)}
                disabled={deleteLoading}
              >
                {t("profilePage.no")}
              </button>
              <button
                className="btn-danger"
                onClick={submitDeleteRequest}
                disabled={deleteLoading}
              >
                {deleteLoading ? t("profilePage.sending") : t("profilePage.yes")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}